import { Link, useRouterState } from '@tanstack/react-router';
import { CircleHelp, LogOut, Search, UserRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Me } from '../api.ts';
import { logout } from '../auth.tsx';
import type { NavArea } from '../navigation.ts';
import { FooterIcon, settingsEntry } from './menu.tsx';

/**
 * O canto direito das faixas do topo (blocos de áreas, foco e planilha): busca, Configuração,
 * Ajuda, a pessoa e Sair. Os ícones levam o nome na dica e para o leitor de tela.
 */
export function UserLinks({
  areas,
  me,
  onSearch,
  search = true,
}: {
  areas: NavArea[];
  me: Me;
  onSearch: () => void;
  search?: boolean;
}) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { settingsArea, settings } = settingsEntry(areas);
  const inSettings = pathname === '/settings' || pathname.startsWith('/settings/');
  return (
    <div className="flex shrink-0 items-center gap-1">
      {search && (
        <button
          type="button"
          onClick={onSearch}
          aria-label={t('nav.search')}
          title={`${t('nav.search')} (Ctrl K)`}
          className="flex size-9 items-center justify-center rounded-md text-muted hover:bg-paper hover:text-ink"
        >
          <Search size={18} strokeWidth={1.75} aria-hidden />
        </button>
      )}
      {settings && (
        <FooterIcon
          to={settings.to}
          label={t(settingsArea!.key)}
          icon={settingsArea!.icon}
          active={inSettings}
        />
      )}
      <FooterIcon
        to="/help"
        label={t('nav.help')}
        icon={CircleHelp}
        active={pathname === '/help'}
      />
      <Link
        to="/account"
        title={`${t('nav.account')} (${me.user.email})`}
        aria-current={pathname === '/account' ? 'page' : undefined}
        className={`flex min-h-9 max-w-44 items-center gap-1.5 rounded-md px-2 text-sm font-medium ${
          pathname === '/account' ? 'ui-nav-active' : 'text-ink hover:bg-paper'
        }`}
      >
        <UserRound size={16} strokeWidth={1.75} aria-hidden className="shrink-0" />
        <span className="truncate">{me.user.displayName}</span>
      </Link>
      <button
        type="button"
        onClick={() => void logout()}
        aria-label={t('auth.logout')}
        title={t('auth.logout')}
        className="flex size-9 items-center justify-center rounded-md text-brand-text hover:bg-paper"
      >
        <LogOut size={16} strokeWidth={1.75} aria-hidden />
      </button>
    </div>
  );
}
